import {
  ServiceAccount,
  TenantAccountBillingStatusResponse,
  TenantAccountStatusResponse,
  GetInvoicesListData,
} from '../../../../apis/types'

export type ServiceAccountWithInvoices = ServiceAccount & {
  invoices: GetInvoicesListData
  invoicesEnriched?: boolean
}

export type BillingStatusMap = { [accountId: string]: TenantAccountBillingStatusResponse }
export type AccountStatusMap = { [accountId: string]: TenantAccountStatusResponse }

/**
 * Badge for list tabs that may be truncated in the tenant detail embed.
 * Prefers `total` when known; otherwise uses shown length and appends `+` at the page cap.
 */
export const previewListBadge = (shown: number, max = 5, total?: number): number | string | undefined => {
  if (total != null) {
    if (total <= 0) return undefined
    return total > max ? `${max}+` : total
  }
  if (shown <= 0) return undefined
  return shown >= max ? `${max}+` : shown
}

const positiveCount = (n: number): number | undefined => (n > 0 ? n : undefined)

/**
 * Tab qty badges from GET /tenant. Invoice badge appears only after TabInvoices
 * enrich (invoicesEnriched). Omit tabs that fetch only after selection (billing
 * events, documents, payments list).
 */
export const getInitialLoadTabBadges = (
  account: ServiceAccountWithInvoices
): {
  serviceAgreements?: number
  accountManagers?: number
  invoices?: number
  incentives?: number
} => ({
  serviceAgreements: positiveCount(account.serviceAgreements.filter((sa) => sa.active === true).length),
  accountManagers: positiveCount((account.accountManagers ?? []).filter((am) => am.isActive !== false).length),
  invoices:
    account.invoicesEnriched === true
      ? positiveCount(account.invoices.total ?? account.invoices.results.length)
      : undefined,
  incentives: positiveCount(
    [
      ...(account.incentivePlans?.tenantAccountIncentivePlans ?? []),
      ...(account.incentivePlans?.utilityAccountIncentivePlans ?? []),
    ].filter((plan) => plan.status === 'ACTIVE').length
  ),
})

/** Operational billing-status reasons that flag an account as needing attention. */
export const NEEDS_ATTENTION_REASONS = new Set([
  'NOT_ON_VNEM',
  'MISSING_BLUE_BILL',
  'MISSING_DOB',
  'SCRAPER_NOT_ACTIVE',
  'NOT_LINKED',
  'NOT_LIVE',
  'VNEM_ALLOC_ISSUE',
  'UTILITY_ISSUE',
  'WRONG_LINK',
  'WRONG_TARIFF',
  'ARBITRATION',
  'BANKRUPTCY',
])

export const parseBalanceAmount = (value: string | number | null | undefined): number | null => {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * Header money chips: prefer tenant DTO ledger balances so values do not jump
 * when the Invoices tab later writes `invoices.summary`. Missing values are $0.
 */
export const getHeaderBalanceAmounts = (
  account: ServiceAccountWithInvoices
): { balanceTotal: number; keBalanceTotal: number; balanceDue: number } => {
  const dtoBilled = parseBalanceAmount(account.billedBalanceTenantToCorp)
  const dtoDue = parseBalanceAmount(account.dueBalanceTenantToCorp)
  const summaryTotal = parseBalanceAmount(account.invoices?.summary?.balanceTotal)
  const summaryDue = parseBalanceAmount(account.invoices?.summary?.balanceDue)
  const currentBilled = parseBalanceAmount(account.currentInvoice?.billedBalanceTenantToCorp)

  return {
    balanceTotal: dtoBilled ?? summaryTotal ?? 0,
    keBalanceTotal: dtoBilled ?? currentBilled ?? summaryTotal ?? 0,
    balanceDue: dtoDue ?? summaryDue ?? 0,
  }
}

export const isAccountUnpaid = (account: ServiceAccountWithInvoices): boolean => {
  const { balanceDue } = getHeaderBalanceAmounts(account)
  if (balanceDue > 0) return true
  if (account.invoicesEnriched !== true) return false
  return account.invoices.results.length > 0 && account.invoices.results[0].paymentStatus === 'UNPAID'
}

export const getBalanceStandingStatus = (account: ServiceAccountWithInvoices): 'UNPAID' | 'CURRENT' =>
  isAccountUnpaid(account) ? 'UNPAID' : 'CURRENT'

export const getOptiRateStatus = (account: ServiceAccount): 'ENROLLED' | 'PENDING' | 'NOT_ENROLLED' => {
  if (account.serviceAgreements.some((sa) => sa.optiRateDetails?.status === 'ENROLLED')) return 'ENROLLED'
  if (account.serviceAgreements.some((sa) => sa.optiRateDetails?.status === 'PENDING')) return 'PENDING'
  return 'NOT_ENROLLED'
}

/** Map TenantAccount.status → finance KE Account badge token. */
export const getKeAccountStatus = (
  accountStatus: string | undefined | null,
  account?: ServiceAccount
): string => {
  // Prefer live /status map over cached GET /tenant DTO.
  const raw = accountStatus ?? account?.accountStatus
  if (raw == null || raw === '') return 'UNKNOWN'
  if (raw === 'ACTIVE') return 'OPEN'
  if (raw === 'PENDING_CLOSED') return 'PENDING_CLOSE'
  return raw
}

/** Remaining cancelation notice days for PENDING_CLOSED (null when not applicable). */
export const getPendingCloseRemainingDays = (
  account: ServiceAccount | undefined,
  accountStatus?: TenantAccountStatusResponse | null
): number | null => {
  // Prefer live /status map over cached GET /tenant DTO.
  const status = accountStatus?.status ?? account?.accountStatus
  if (status !== 'PENDING_CLOSED') return null

  const noticeDays =
    accountStatus?.cancellationNoticeDays ?? account?.cancellationNoticeDays ?? null
  const startedAtRaw =
    accountStatus?.pendingCloseStartedAt ?? account?.pendingCloseStartedAt ?? null
  if (noticeDays == null || startedAtRaw == null) return null

  const started = new Date(startedAtRaw).getTime()
  if (!Number.isFinite(started)) return null
  const elapsedDays = Math.floor((Date.now() - started) / (24 * 60 * 60 * 1000))
  return Math.max(0, noticeDays - elapsedDays)
}

export const getPendingCloseTooltip = (
  account: ServiceAccount | undefined,
  accountStatus?: TenantAccountStatusResponse | null
): string | undefined => {
  const remaining = getPendingCloseRemainingDays(account, accountStatus)
  if (remaining == null) return undefined
  const reason =
    accountStatus?.cancellationReason ?? account?.cancellationReason ?? undefined
  const reasonPart = reason != null ? ` Reason: ${reason.replace(/_/g, ' ').toLowerCase()}.` : ''
  if (remaining === 0) {
    return `Notice period elapsed.${reasonPart} Auto-close runs when balances and payment plans allow.`
  }
  return `${remaining} day${remaining === 1 ? '' : 's'} remaining in cancelation notice.${reasonPart}`
}

export const getUtilityAccountStatus = (account: ServiceAccount): 'ACTIVE' | 'INACTIVE' | 'UNKNOWN' => {
  if (account.utilityStanding != null) return account.utilityStanding
  const agreements = account.serviceAgreements ?? []
  if (agreements.length === 0) return 'UNKNOWN'
  // CLOSED / PENDING_CLOSED are never Active — even when active=true (PENDING_CLOSED still bills).
  if (
    agreements.some(
      (sa) =>
        sa.active !== false &&
        sa.serviceStatus !== 'CLOSED' &&
        sa.serviceStatus !== 'PENDING_CLOSED' &&
        (sa.serviceStatus === 'ACTIVE' || sa.active === true)
    )
  ) {
    return 'ACTIVE'
  }
  if (
    agreements.every(
      (sa) => sa.serviceStatus === 'CLOSED' || sa.serviceStatus === 'PENDING_CLOSED' || sa.active === false
    )
  ) {
    return 'INACTIVE'
  }
  return 'UNKNOWN'
}

export type ArStanding = 'CURRENT' | 'PAST_DUE' | 'PAYMENT_PLAN_CURRENT' | 'PAYMENT_PLAN_PAST_DUE'

const AR_STANDING_LABELS: Record<ArStanding, string> = {
  CURRENT: 'Current',
  PAST_DUE: 'Past Due',
  PAYMENT_PLAN_CURRENT: 'Payment Plan Current',
  PAYMENT_PLAN_PAST_DUE: 'Payment Plan Past Due',
}

export const getArBillingStanding = (account: ServiceAccountWithInvoices): ArStanding => {
  if (account.arStanding != null) return account.arStanding

  const plans = [
    ...(account.incentivePlans?.tenantAccountIncentivePlans ?? []),
    ...(account.incentivePlans?.utilityAccountIncentivePlans ?? []),
  ].filter((plan) => plan.type === 'PAYMENT_PLAN' && plan.status === 'ACTIVE')

  if (plans.length > 0) {
    const pastDue = plans.some((plan) => (plan.installments ?? []).some((i) => i.status === 'PAST_DUE'))
    return pastDue ? 'PAYMENT_PLAN_PAST_DUE' : 'PAYMENT_PLAN_CURRENT'
  }

  if ((account.daysPastDue ?? 0) > 0) return 'PAST_DUE'
  return 'CURRENT'
}

/** Badge label for Billing Standing; appends DPD when past due. */
export const getArBillingStandingBadgeLabel = (
  status: ArStanding,
  daysPastDue?: number | null
): string => {
  const base = AR_STANDING_LABELS[status]
  if (
    (status === 'PAST_DUE' || status === 'PAYMENT_PLAN_PAST_DUE') &&
    daysPastDue != null &&
    daysPastDue > 0
  ) {
    return `${base} · ${daysPastDue} DPD`
  }
  return base
}

/**
 * VNEM chip: prefer DTO `vnemStatus` (authoritative from computeVnemStatus).
 * Fallback only covers ON_HOLD / OFF_TABLE / UNKNOWN from billing ops —
 * it cannot derive PENDING_ON / PENDING_OFF without alloc submission state.
 * Ops loaded without NOT_ON_VNEM and without DTO → UNKNOWN (not optimistic ON_TABLE).
 */
export const getVnemStatus = (
  billingStatus: TenantAccountBillingStatusResponse | undefined,
  account?: ServiceAccount
): string => {
  if (account?.vnemStatus != null) return account.vnemStatus

  if (billingStatus?.current == null) return 'UNKNOWN'
  const reasons = billingStatus.current.reasons ?? []
  const ops = billingStatus.current.status
  const vnemReason = reasons.includes('NOT_ON_VNEM') || reasons.includes('VNEM_ALLOC_ISSUE')
  if ((ops === 'ONHOLD' || ops === 'BLOCKED') && vnemReason) return 'ON_HOLD'
  if (reasons.includes('NOT_ON_VNEM')) return 'OFF_TABLE'
  return 'UNKNOWN'
}

export const isNeedsAttention = (
  account: ServiceAccountWithInvoices,
  billingStatus: TenantAccountBillingStatusResponse | undefined,
  accountStatus: TenantAccountStatusResponse | undefined
): boolean => {
  if (isAccountUnpaid(account)) return true

  const standing = billingStatus?.current?.status ?? account.billingOpsStatus?.status
  if (standing === 'BLOCKED' || standing === 'ONHOLD') return true

  const status = accountStatus?.status ?? account.accountStatus
  if (status === 'ONHOLD' || status === 'PENDING_CLOSED') return true

  const reasons = billingStatus?.current?.reasons ?? account.billingOpsStatus?.reasons ?? []
  if (reasons.some((reason) => NEEDS_ATTENTION_REASONS.has(reason))) return true

  return false
}

/** Fallback option lists when billing-options API values are unavailable.
 * Values are Badge status tokens so tooltip legends can reuse chip colors. */
export const STATUS_CHIP_POSSIBLE_VALUES = {
  balance: ['CURRENT', 'UNPAID'],
  /** Balance Due is amount-based — use HEADER_BALANCE_CHIP_TOOLTIPS.balanceDue. */
  balanceDue: [] as readonly string[],
  keAccount: ['OPEN', 'PENDING_CLOSE', 'CLOSED', 'ONHOLD', 'PENDING_ACTIVE', 'NONE'],
  utilityAccount: ['ACTIVE', 'INACTIVE', 'UNKNOWN'],
  billingStanding: ['CURRENT', 'PAST_DUE', 'PAYMENT_PLAN_CURRENT', 'PAYMENT_PLAN_PAST_DUE'],
  billingOpsStatus: [
    'NONE',
    'PENDING_ACTIVE',
    'ACTIVE',
    'ONHOLD',
    'BLOCKED',
    'PENDING_STOP',
    'STOPPED',
  ],
  billingMode: [
    'CONSOLIDATED',
    'STANDALONE',
    'CONSOLIDATED_ESTIMATED',
    'STANDALONE_ESTIMATED',
    'BALANCE_ONLY',
    'FINAL',
    'UNKNOWN',
  ],
  vnemStatus: ['ON_TABLE', 'PENDING_OFF', 'OFF_TABLE', 'PENDING_ON', 'ON_HOLD', 'UNKNOWN'],
  paperless: ['ENABLED', 'DISABLED'],
  autopay: ['ENABLED', 'DISABLED'],
  optiRate: ['ENROLLED', 'PENDING', 'NOT_ENROLLED'],
} as const

export type StatusChipKey = keyof typeof STATUS_CHIP_POSSIBLE_VALUES

/** Amount-based header chips — prose tooltips, not enum legends. */
export const HEADER_BALANCE_CHIP_TOOLTIPS = {
  balanceTotal:
    'Outstanding billed balance (AR). Includes charges that are billed but not yet due.',
  keBalanceTotal:
    'King Energy ledger billed balance (tenant → King). Same billed AR as Balance Total when both come from the tenant account.',
  balanceDue:
    'Amount due now to stay current. Excludes billed charges whose due date has not arrived and payment-plan installments not yet due. Same live calculation as tenant detail.',
} as const

/** Softened billing-mode labels for Admin chips (stored enum unchanged). */
export const BILLING_MODE_CHIP_LABELS: Record<string, string> = {
  CONSOLIDATED: 'Consolidated',
  STANDALONE: 'Standalone',
  CONSOLIDATED_ESTIMATED: 'Consolidated Estimated',
  STANDALONE_ESTIMATED: 'Standalone Estimated',
  BALANCE_ONLY: 'Balance only',
  FINAL: 'Final',
  UNKNOWN: 'Unknown',
}

/**
 * Billing Mode chip token + label. When account is Final (CLOSED + settled),
 * show Final without mutating stored BillingMode.
 */
export const getBillingModeChip = (
  account: ServiceAccount
): { status: string; displayLabel: string } => {
  if (account.isFinalAccount === true) {
    return { status: 'FINAL', displayLabel: 'Final' }
  }
  const mode = account.billingConfiguration?.tenantAccountConfiguration?.billingMode || 'UNKNOWN'
  return {
    status: mode,
    displayLabel: BILLING_MODE_CHIP_LABELS[mode] ?? mode,
  }
}

/** True when the legend value matches the chip’s current status token. */
export const isStatusChipLegendActive = (
  value: string,
  currentStatus?: string | null
): boolean => currentStatus != null && currentStatus !== '' && value === currentStatus

export interface TenantKpiRollup {
  totalBalance: number
  totalDue: number
  /** Absolute sum of negative Balance Total amounts excluded from the rollup. */
  excludedCreditTotal: number
  /** Absolute sum of negative Balance Due amounts excluded from the rollup. */
  excludedCreditDue: number
  activeAccounts: number
  totalAccounts: number
  activeServiceAgreements: number
  totalServiceAgreements: number
  optiRateEnrolled: number
  optiRateTotal: number
  /** False until billing + account status maps have been populated for loaded accounts. */
  statusesLoaded: boolean
}

export const computeTenantKpis = (
  accounts: ServiceAccountWithInvoices[],
  _billingStatuses: BillingStatusMap,
  accountStatuses: AccountStatusMap,
  statusesLoaded: boolean
): TenantKpiRollup => {
  let totalBalance = 0
  let totalDue = 0
  let excludedCreditTotal = 0
  let excludedCreditDue = 0
  let activeAccounts = 0
  let activeServiceAgreements = 0
  let totalServiceAgreements = 0
  let optiRateEnrolled = 0

  for (const account of accounts) {
    const balances = getHeaderBalanceAmounts(account)
    // Credits do not offset collectible AR on sibling accounts (WEB-7072).
    if (balances.balanceTotal < 0) excludedCreditTotal += -balances.balanceTotal
    if (balances.balanceDue < 0) excludedCreditDue += -balances.balanceDue
    totalBalance += Math.max(0, balances.balanceTotal)
    totalDue += Math.max(0, balances.balanceDue)
    const status = accountStatuses[account.id]?.status ?? account.accountStatus
    if (status === 'ACTIVE') activeAccounts += 1
    totalServiceAgreements += account.serviceAgreements.length
    activeServiceAgreements += account.serviceAgreements.filter((sa) => sa.active === true).length
    if (getOptiRateStatus(account) === 'ENROLLED') optiRateEnrolled += 1
  }

  return {
    totalBalance,
    totalDue,
    excludedCreditTotal,
    excludedCreditDue,
    activeAccounts,
    totalAccounts: accounts.length,
    activeServiceAgreements,
    totalServiceAgreements,
    optiRateEnrolled,
    optiRateTotal: accounts.length,
    statusesLoaded,
  }
}
